import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateMFAPhone, verifyPhoneNumber, setMFAOptions } from "actions/multifactor";
import useTranslate from "hooks/use-translate-language";
import { isValidPhoneNumber } from "utils/validation";
import { useFormik, FormikProvider } from "formik";
import { LoadingButton, Spinner } from "components/ui";
import * as Yup from "yup";
import { VerifiedUser } from "@mui/icons-material";
import { FormControlLabel, Alert, Switch } from "@mui/material";
import PropTypes from "prop-types";
import { PhoneNumberInput, TextInput } from "components/form";
import { formatPhone, normalizePhone } from "utils/misc";
import { NotificationManager } from "react-notifications";

const PHASE = {
  ENTER_PHONE: "enter_phone",
  VERIFY_CODE: "verify_code",
  VERIFIED: "verified"
};

const RESEND_COUNTDOWN = 120;

const getInitialPhase = patientInfo => {
  if (patientInfo.mfa_phone_number_verified) return PHASE.VERIFIED;
  if (patientInfo.mfa_phone_number) return PHASE.VERIFY_CODE;
  return PHASE.ENTER_PHONE;
};

const getRemainingCountdown = () => {
  try {
    const start = parseInt(localStorage.getItem("mfaPhoneCountdownStart"), 10);
    if (!start) return 0;
    const elapsed = Math.floor((Date.now() - start) / 1000);
    return Math.max(0, RESEND_COUNTDOWN - elapsed);
  } catch {
    return 0;
  }
};

export default function MultifactorOptionsForms({ patientInfo, onPhoneUpdated }) {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const [phase, setPhase] = useState(() => getInitialPhase(patientInfo));
  const [pendingPhone, setPendingPhone] = useState(patientInfo.mfa_phone_number || "");
  const [smsError, setSmsError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [timeLeft, setTimeLeft] = useState(() => getRemainingCountdown());
  const [mfaEnabled, setMfaEnabled] = useState(patientInfo.mfa_enabled || false);
  const [togglingMFA, setTogglingMFA] = useState(false);

  // Countdown tick
  useEffect(() => {
    if (!timeLeft) return;
    const id = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1;
        if (next <= 0) localStorage.removeItem("mfaPhoneCountdownStart");
        return Math.max(0, next);
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timeLeft]);

  const startCountdown = () => {
    localStorage.setItem("mfaPhoneCountdownStart", Date.now().toString());
    setTimeLeft(RESEND_COUNTDOWN);
  };

  // ── Phase 1: enter phone number ──────────────────────────────────────────
  const fkPhone = useFormik({
    initialValues: { phone_number: patientInfo.mfa_phone_number || "" },
    validationSchema: Yup.object().shape({
      phone_number: Yup.string()
        .required(translate("required"))
        .test("valid", translate("invalidPhoneNumber"), v => isValidPhoneNumber(v))
    }),
    onSubmit: async ({ phone_number }) => {
      // If the patient re-entered their already-verified number, skip re-verification
      if (
        phone_number === patientInfo.mfa_phone_number &&
        patientInfo.mfa_phone_number_verified
      ) {
        setPhase(PHASE.VERIFIED);
        return;
      }
      setSmsError("");
      const result = await dispatch(updateMFAPhone({ phone_number }));
      if (!result) {
        setSmsError(translate("smsFailedError"));
      } else {
        setPendingPhone(phone_number);
        setPhase(PHASE.VERIFY_CODE);
        startCountdown();
      }
    }
  });

  // ── Phase 2: enter verification code ────────────────────────────────────
  const fkCode = useFormik({
    initialValues: { verification_code: "" },
    onSubmit: async ({ verification_code }) => {
      setCodeError("");
      try {
        const result = await dispatch(
          verifyPhoneNumber({ Code: `${verification_code}` })
        );
        if (result === true) {
          localStorage.removeItem("mfaPhoneCountdownStart");
          setTimeLeft(0);
          NotificationManager.success(translate("phoneVerificationSucceded"));
          setPhase(PHASE.VERIFIED);
          onPhoneUpdated();
        } else {
          setCodeError(translate("incorrectCode"));
        }
      } catch {
        setCodeError(translate("incorrectCode"));
      }
    }
  });

  const handleResend = async () => {
    setSmsError("");
    const result = await dispatch(updateMFAPhone({ phone_number: pendingPhone }));
    if (result) {
      startCountdown();
      NotificationManager.success(translate("verificationCodeSent"));
    } else {
      setSmsError(translate("smsFailedError"));
    }
  };

  const handleChangePhone = () => {
    fkPhone.setFieldValue("phone_number", pendingPhone);
    fkCode.resetForm();
    setSmsError("");
    setCodeError("");
    setPhase(PHASE.ENTER_PHONE);
  };

  const handleCancel = () => {
    fkPhone.setFieldValue("phone_number", patientInfo.mfa_phone_number || "");
    fkCode.resetForm();
    setSmsError("");
    setCodeError("");
    setPhase(PHASE.VERIFIED);
  };

  const canCancel = patientInfo.mfa_phone_number_verified;

  const handleMFAToggle = async e => {
    const enabled = e.target.checked;
    setTogglingMFA(true);
    await dispatch(setMFAOptions({ Enabled: enabled }));
    setMfaEnabled(enabled);
    setTogglingMFA(false);
  };

  return (
    <>
      {/* ── MFA enable/disable toggle ── */}
      <form className="multifactor-panel">
        {togglingMFA ? (
          <>
            <Spinner style={{ marginRight: "1rem" }} size="1rem" />
            <div className="multifactor-label">{translate("useMultifactor")}</div>
          </>
        ) : (
          <FormControlLabel
            control={
              <Switch
                name="use_multifactor"
                checked={mfaEnabled}
                onChange={handleMFAToggle}
                inputProps={{ "aria-label": translate("useMultifactor") }}
              />
            }
            label={translate("useMultifactor")}
            className="multifactor-label"
          />
        )}
      </form>

      {/* ── Phone verification section (only shown when MFA on) ── */}
      {mfaEnabled && !togglingMFA && (
        <div className="verify-phone-number-panel">

          {/* ── Phase 1: enter phone ── */}
          {phase === PHASE.ENTER_PHONE && (
            <FormikProvider value={fkPhone}>
            <form
              className="phone-number-form"
              onSubmit={fkPhone.handleSubmit}
            >
              <p className="verify-phone-number-label">
                {translate("enterPhoneForMFA")}
              </p>
              <div className="phone-number-panel">
                <PhoneNumberInput
                  id="phone_number"
                  name="phone_number"
                  label={translate("verifyPhoneNumber")}
                  type="tel"
                  onBlur={fkPhone.handleBlur}
                  value={formatPhone(fkPhone.values.phone_number) || ""}
                  onChange={e => {
                    fkPhone.setFieldValue(
                      "phone_number",
                      normalizePhone(e.target.value)
                    );
                  }}
                  error={
                    !!fkPhone.touched.phone_number && !!fkPhone.errors.phone_number
                  }
                  helperText={
                    fkPhone.touched.phone_number && fkPhone.errors.phone_number
                  }
                />
                <LoadingButton
                  color="primary"
                  type="submit"
                  isFetching={fkPhone.isSubmitting}
                  disabled={
                    !fkPhone.isValid ||
                    !fkPhone.values.phone_number ||
                    timeLeft > 0
                  }
                >
                  {timeLeft > 0
                    ? translate("waiting").replace("X", timeLeft)
                    : translate("sendCode")}
                </LoadingButton>
              </div>
              {canCancel && (
                <LoadingButton
                  color="secondary"
                  type="button"
                  onClick={handleCancel}
                  style={{ marginTop: 8 }}
                >
                  {translate("cancel")}
                </LoadingButton>
              )}
              {smsError && (
                <Alert severity="error" style={{ marginTop: 8 }}>
                  {smsError}
                </Alert>
              )}
            </form>
            </FormikProvider>
          )}

          {/* ── Phase 2: enter verification code ── */}
          {phase === PHASE.VERIFY_CODE && (
            <FormikProvider value={fkCode}>
            <>
              <p className="verify-phone-number-label">
                {translate("codeSentTo").replace("{phone}", formatPhone(pendingPhone) || pendingPhone)}
              </p>
              <form
                className="verification-code-form"
                onSubmit={fkCode.handleSubmit}
              >
                <div className="phone-number-panel">
                  <TextInput
                    id="verification_code"
                    name="verification_code"
                    label={translate("verificationCode")}
                    type="text"
                    value={fkCode.values.verification_code || ""}
                    onChange={e =>
                      fkCode.setFieldValue("verification_code", e.target.value)
                    }
                    error={!!codeError}
                    helperText={codeError}
                  />
                  <LoadingButton
                    color="primary"
                    type="submit"
                    isFetching={fkCode.isSubmitting}
                    disabled={
                      !fkCode.values.verification_code ||
                      fkCode.values.verification_code.length !== 6
                    }
                  >
                    {translate("verify")}
                  </LoadingButton>
                </div>
              </form>

              <div style={{ marginTop: 12, display: "flex", gap: 16, alignItems: "center" }}>
                {timeLeft > 0 ? (
                  <span className="verify-phone-number-label">
                    {translate("waiting").replace("X", timeLeft)}
                  </span>
                ) : (
                  <LoadingButton
                    color="secondary"
                    type="button"
                    onClick={handleResend}
                  >
                    {translate("resendCode")}
                  </LoadingButton>
                )}
                <LoadingButton
                  color="secondary"
                  type="button"
                  onClick={handleChangePhone}
                >
                  {translate("changePhoneNumber")}
                </LoadingButton>
                {canCancel && (
                  <LoadingButton
                    color="secondary"
                    type="button"
                    onClick={handleCancel}
                  >
                    {translate("cancel")}
                  </LoadingButton>
                )}
              </div>
              {smsError && (
                <Alert severity="error" style={{ marginTop: 8 }}>
                  {smsError}
                </Alert>
              )}
            </>
            </FormikProvider>
          )}

          {/* ── Verified state ── */}
          {phase === PHASE.VERIFIED && (
            <div className="phone-number-panel" style={{ alignItems: "center" }}>
              <VerifiedUser htmlColor="green" />
              <span className="verify-phone-number-label" style={{ marginLeft: 8 }}>
                {translate("alreadyVerified")}: {formatPhone(patientInfo.mfa_phone_number) || patientInfo.mfa_phone_number}
              </span>
              <LoadingButton
                color="secondary"
                type="button"
                onClick={handleChangePhone}
                style={{ marginLeft: 16 }}
              >
                {translate("changePhoneNumber")}
              </LoadingButton>
            </div>
          )}
        </div>
      )}
    </>
  );
}

MultifactorOptionsForms.propTypes = {
  patientInfo: PropTypes.object.isRequired,
  onPhoneUpdated: PropTypes.func.isRequired
};
